import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Input, Tree } from 'syngenta-digital-cropwise-react-ui-kit';
import LoadingSpinner from 'components/LoadingSpinner';
import { obtainAllKeys } from 'screens/Reports/helpers/helperFunctions';
import { useFormikContext } from 'formik';

const groupByPlansDefaultPropMappedData = [];
const GroupByPlans = ({
  mappedData = groupByPlansDefaultPropMappedData,
  disabled = false,
  loading = false,
  handleCheck,
  checked,
  setChecked
}) => {
  const [filterText, setFilterText] = useState();
  const [plansNode, setPlansNode] = useState([]);
  const [nodesFiltered, setNodesFiltered] = useState(plansNode);
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    let mappedFft = [];
    if (loading === false && mappedData) {
      mappedFft = mappedData?.map(plan => ({
        value: plan.planId,
        label: plan.name,
        type: 'plan'
      }));
    }
    if (mappedData?.length) {
      mappedFft = mappedFft.map(plan => ({
        ...plan
      }));

      setPlansNode(mappedFft);
    }
  }, [mappedData, loading]);

  useEffect(() => {
    handleCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansNode]);

  const filterNodes = useCallback(
    (filtered, node) => {
      const children = (node.fields || []).reduce(
        (acc, current) => filterNodes(acc, current),
        []
      );
      if (
        node.label
          .toLocaleLowerCase()
          .indexOf(filterText?.toLocaleLowerCase()) > -1 ||
        children.length
      ) {
        filtered.push({ ...node, children });
      }

      return filtered;
    },
    [filterText]
  );

  const filterTree = () => {
    if (!filterText?.length) {
      setNodesFiltered(plansNode);
      return;
    }

    setNodesFiltered(nodes =>
      nodes.reduce((acc, current) => filterNodes(acc, current), [])
    );
  };

  const onFilterChange = e => {
    if (e.target.value.length >= 3) {
      setFilterText(e.target.value);
      filterTree();
    } else {
      setFilterText('');
    }
  };

  useEffect(() => {
    if (!filterText) {
      setNodesFiltered(plansNode);
      return;
    }

    setNodesFiltered(prevState =>
      prevState.reduce((acc, current) => filterNodes(acc, current), [])
    );
  }, [filterText, filterNodes, plansNode]);

  useEffect(() => {
    setChecked(obtainAllKeys(plansNode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansNode]);

  const planKeys = obtainAllKeys(plansNode);

  const handleSelectAllCheckBox = () => {
    if (checked.length === planKeys.length) {
      setChecked([]);
    } else {
      setChecked(planKeys);
    }
  };

  useEffect(() => {
    setFieldValue('selectedPlans', [...new Set(checked)]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  return (
    <div className="filter-container">
      {!loading ? (
        <>
          {mappedData?.length > 0 && (
            <>
              <div
                className="my-2"
                data-testid="plans-filter-container"
                style={{ display: 'none' }}
              >
                <Input
                  allowClear
                  placeholder="Search..."
                  onChange={onFilterChange}
                />
              </div>
              <div className="mt-2 flex">
                <Checkbox
                  onChange={handleSelectAllCheckBox}
                  checked={checked?.length === planKeys?.length}
                >
                  Select All
                </Checkbox>
              </div>

              <Tree
                checkable
                treeData={nodesFiltered.map(node => ({
                  ...node,
                  key: node.value,
                  title: node.label
                }))}
                checkedKeys={checked ?? []}
                onCheck={keys => handleCheck(keys)}
                selectable={false}
                showIcon={false}
                disabled={disabled}
              />
            </>
          )}
        </>
      ) : (
        <LoadingSpinner
          message=""
          size={25}
          inline
          className="h-full"
          label="Loading plans"
        />
      )}
    </div>
  );
};

GroupByPlans.propTypes = {
  mappedData: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  handleCheck: PropTypes.func.isRequired,
  checked: PropTypes.arrayOf(PropTypes.string).isRequired,
  setChecked: PropTypes.func.isRequired
};

export default GroupByPlans;
